const gallerySelector = 'media-gallery[data-ur-part-gallery]';
const triggerSelector = '.ur-part-gallery__trigger';
const imageSelector = '.product-media-container--image .product-media[data-media-id]';
const labels = ['viewerLabel', 'viewerOpenLabel', 'viewerCloseLabel', 'viewerPreviousLabel', 'viewerNextLabel', 'viewerErrorLabel'];
const icons = {
  close: '<path d="m6 6 12 12M6 18 18 6"/>',
  previous: '<path d="m14 6-6 6 6 6"/>',
  next: '<path d="m10 6 6 6-6 6"/>',
  expand: '<path d="M9 4H4v5m11-5h5v5M4 15v5h5m11-5v5h-5"/>',
};
let viewer;
let active;
let pointer;
let pending = false;
const galleriesToUpdate = new Set();

function icon(name) {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${icons[name]}</svg>`;
}

function mediaFor(gallery) {
  return [...gallery.querySelectorAll(imageSelector)].filter((media) =>
    media.closest(gallerySelector) === gallery &&
    !media.closest('zoom-dialog, dialog') &&
    !media.closest('[hidden]:not([reveal])') &&
    media.querySelector('img.product-media__image')
  );
}

function enhance(gallery) {
  if (!gallery.isConnected || gallery.querySelector('zoom-dialog') || labels.some((label) => !gallery.dataset[label]?.trim())) return;
  for (const media of mediaFor(gallery)) {
    let button = media.querySelector(triggerSelector);
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'ur-part-gallery__trigger';
      button.setAttribute('aria-haspopup', 'dialog');
      button.innerHTML = `<span class="ur-part-gallery__expand">${icon('expand')}</span>`;
      media.classList.add('ur-part-gallery__image');
      media.append(button);
    }
    const image = media.querySelector('img.product-media__image');
    button.setAttribute('aria-label', [gallery.dataset.viewerOpenLabel, image.alt].filter(Boolean).join(': '));
    button.tabIndex = media.closest('[aria-hidden="true"], [inert]') ? -1 : 0;
  }
}

function queueGallery(gallery) {
  if (gallery) galleriesToUpdate.add(gallery);
  if (pending) return;
  pending = true;
  queueMicrotask(() => {
    pending = false;
    for (const item of galleriesToUpdate) enhance(item);
    galleriesToUpdate.clear();
    if (active && !active.gallery.isConnected) viewer.dialog.close();
  });
}

function createButton(name) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `ur-part-viewer__button ur-part-viewer__${name}`;
  button.innerHTML = icon(name);
  return button;
}

function createViewer() {
  const dialog = document.createElement('dialog');
  dialog.className = 'ur-part-viewer';
  dialog.setAttribute('aria-modal', 'true');
  const header = document.createElement('div');
  header.className = 'ur-part-viewer__header';
  const title = document.createElement('p');
  title.className = 'ur-part-viewer__title';
  const close = createButton('close');
  close.autofocus = true;
  header.append(title, close);
  const stage = document.createElement('div');
  stage.className = 'ur-part-viewer__stage';
  const error = document.createElement('p');
  error.className = 'ur-part-viewer__error';
  error.setAttribute('role', 'alert');
  error.hidden = true;
  const footer = document.createElement('div');
  footer.className = 'ur-part-viewer__footer';
  const previous = createButton('previous');
  const next = createButton('next');
  const counter = document.createElement('span');
  counter.className = 'ur-part-viewer__counter';
  counter.setAttribute('role', 'status');
  counter.setAttribute('aria-live', 'polite');
  counter.setAttribute('aria-atomic', 'true');
  footer.append(previous, counter, next);
  dialog.append(header, stage, footer);
  close.addEventListener('click', () => dialog.close());
  previous.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      const buttons = [close, previous, next].filter((button) => !button.hidden && !button.disabled);
      const index = buttons.indexOf(document.activeElement);
      event.preventDefault();
      buttons[(index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length].focus();
      return;
    }
    if (!active || event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === 'ArrowLeft' ? -1 : 1;
    move(dialog.dir === 'rtl' ? -direction : direction);
  });
  let backdropDown = false;
  const outside = (event) => {
    const rect = dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  };
  dialog.addEventListener('pointerdown', (event) => {
    backdropDown = event.target === dialog && outside(event);
  });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog && backdropDown && outside(event)) dialog.close();
    backdropDown = false;
  });
  dialog.addEventListener('close', () => {
    const opener = active?.opener;
    active = undefined;
    stage.replaceChildren();
    stage.removeAttribute('aria-busy');
    if (opener?.isConnected && opener.getClientRects().length) opener.focus({ preventScroll: true });
  });
  document.body.append(dialog);
  return { dialog, title, close, stage, error, previous, next, counter };
}

function showImage() {
  if (!active) return;
  const source = active.images[active.index];
  const image = document.createElement('img');
  image.className = 'ur-part-viewer__image';
  image.alt = source.alt;
  image.decoding = 'async';
  image.loading = 'eager';
  if (source.width && source.height) {
    image.width = source.width;
    image.height = source.height;
  }
  image.sizes = '(max-width: 600px) calc(100vw - 48px), (max-width: 860px) calc(100vw - 64px), 788px';
  viewer.stage.setAttribute('aria-busy', 'true');
  viewer.error.hidden = true;
  viewer.stage.replaceChildren(image, viewer.error);
  viewer.counter.textContent = `${active.index + 1} / ${active.images.length}`;
  const single = active.images.length < 2;
  viewer.previous.hidden = single;
  viewer.next.hidden = single;
  let retried = false;
  const current = () => active && viewer.stage.firstElementChild === image;
  image.addEventListener('load', () => {
    if (current()) viewer.stage.removeAttribute('aria-busy');
  });
  image.addEventListener('error', () => {
    if (!current()) return;
    const fallback = source.getAttribute('data_max_resolution');
    if (!retried && fallback && fallback !== image.src) {
      retried = true;
      image.removeAttribute('srcset');
      image.src = fallback;
      return;
    }
    image.hidden = true;
    viewer.error.textContent = active.gallery.dataset.viewerErrorLabel;
    viewer.error.hidden = false;
    viewer.stage.removeAttribute('aria-busy');
  });
  if (source.srcset) image.srcset = source.srcset;
  image.src = source.currentSrc || source.src;
  if (image.complete && image.naturalWidth) viewer.stage.removeAttribute('aria-busy');
}

function move(offset) {
  if (!active || active.images.length < 2) return;
  active.index = (active.index + offset + active.images.length) % active.images.length;
  showImage();
}

function open(button) {
  const gallery = button.closest(gallerySelector);
  if (!gallery || gallery.querySelector('zoom-dialog') || active) return;
  const media = button.closest('.product-media[data-media-id]');
  const seen = new Set();
  const entries = mediaFor(gallery).filter((item) => {
    if (seen.has(item.dataset.mediaId)) return false;
    seen.add(item.dataset.mediaId);
    return true;
  });
  const index = entries.findIndex((item) => item.dataset.mediaId === media?.dataset.mediaId);
  if (index < 0) return;
  viewer ||= createViewer();
  active = { gallery, opener: button, index, images: entries.map((item) => item.querySelector('img.product-media__image')) };
  viewer.dialog.setAttribute('aria-label', gallery.dataset.viewerLabel);
  viewer.dialog.dir = getComputedStyle(gallery).direction;
  viewer.title.textContent = gallery.dataset.viewerLabel;
  viewer.close.setAttribute('aria-label', gallery.dataset.viewerCloseLabel);
  viewer.previous.setAttribute('aria-label', gallery.dataset.viewerPreviousLabel);
  viewer.next.setAttribute('aria-label', gallery.dataset.viewerNextLabel);
  showImage();
  viewer.dialog.showModal();
  viewer.close.focus({ preventScroll: true });
}

function initialize() {
  if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
  document.querySelectorAll(gallerySelector).forEach(enhance);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.target instanceof Element) {
        const gallery = record.target.closest(gallerySelector);
        if (gallery) queueGallery(gallery);
      }
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(gallerySelector)) queueGallery(node);
        node.querySelectorAll(gallerySelector).forEach(queueGallery);
      }
    }
    if (active && !active.gallery.isConnected) viewer.dialog.close();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'hidden', 'reveal', 'inert'] });
  window.addEventListener('pointerdown', (event) => {
    const button = event.target instanceof Element ? event.target.closest(triggerSelector) : null;
    pointer = button ? { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false } : undefined;
  }, { capture: true, passive: true });
  window.addEventListener('pointermove', (event) => {
    if (pointer?.id === event.pointerId && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 8) pointer.moved = true;
  }, { capture: true, passive: true });
  window.addEventListener('pointercancel', (event) => {
    if (pointer?.id === event.pointerId) pointer.moved = true;
  }, { capture: true, passive: true });
  window.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest(triggerSelector) : null;
    if (!button || event.defaultPrevented || (event.detail > 0 && pointer?.moved)) return;
    event.preventDefault();
    open(button);
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
else initialize();
